import { Button } from "@chakra-ui/button";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { Box, Divider, Flex, Heading, Text, VStack } from "@chakra-ui/layout";
import { useMediaQuery } from "@chakra-ui/media-query";
import { useColorMode, useColorModeValue } from "@chakra-ui/react";
import { default as React, useCallback } from "react";
import useQuote from "../../hooks/useQuote";
import { darkMode, lightMode } from "../../lib/models/color-mode-colors";
import { RentalCalculation } from "../../lib/models/rental/rental-calculation.model";
import { formatDateLocal } from "../../lib/number-transforms";
import { InfoCircleOutline } from "../icons/InfoCircleOutline";
import { TimesCircleOutline } from "../icons/TimesCircleOutline";
import { WarningTriangleOutline } from "../icons/WarningTriangleOutline";
import FormCalcStatusList from "./FormCalcStatusList";

function FormCalcSummary() {
  const quoteContext = useQuote();
  const { state } = quoteContext;
  const { calculationWarnings, metadata, quoteType, quoteCalculation } = state;
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLargerThan1000] = useMediaQuery("(min-width: 992px)");
  const { colorMode } = useColorMode();
  const bgColor = useColorModeValue(lightMode[100], darkMode[200]);

  const hasWarnings = useCallback(() => {
    return calculationWarnings && Object.keys(calculationWarnings).length > 0;
  }, [calculationWarnings]);

  React.useEffect(() => {
    isLargerThan1000 ? setIsOpen(true) : setIsOpen(false);
  }, [isLargerThan1000]);

  return (
    <Box
      background={bgColor}
      padding={4}
      borderRadius="md"
      boxShadow="sm"
      minW={275}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Heading size="sm">Calculation Summary</Heading>
        <Button
          display={{ base: "inline-flex", lg: "none" }}
          rightIcon={
            isOpen ? (
              <ChevronUpIcon height={6} width={6} />
            ) : (
              <ChevronDownIcon height={6} width={6} />
            )
          }
          onClick={() => setIsOpen(!isOpen)}
          variant="unstyled"
          size="sm"
        >
          {isOpen ? "Collapse" : "Expand"}
        </Button>
      </Flex>

      {isOpen && (
        <>
          <>
            {hasWarnings() && <Divider paddingBottom={3} />}

            <VStack spacing={6} alignItems="flex-start" marginTop={4}>
              <FormCalcStatusList
                label="Calculation Errors"
                icon={<TimesCircleOutline marginRight={1} h={4} w={4} />}
                items={calculationWarnings?.calculation}
                colorScheme={colorMode === "light" ? "red" : "redDark"}
              />

              <FormCalcStatusList
                label="Applied Overrides"
                icon={<WarningTriangleOutline marginRight={1} h={4} w={4} />}
                items={calculationWarnings?.overridePricing}
                colorScheme={colorMode === "light" ? "yellow" : "yellowDark"}
              />

              <FormCalcStatusList
                label="Validation Warnings"
                icon={<WarningTriangleOutline marginRight={1} h={4} w={4} />}
                items={calculationWarnings?.warnings}
                colorScheme={colorMode === "light" ? "yellow" : "yellowDark"}
              />
              <FormCalcStatusList
                label="Applied Fee LLPA's"
                icon={<InfoCircleOutline marginRight={1} h={4} w={4} />}
                items={calculationWarnings?.feeLLPAsFactored}
                colorScheme={colorMode === "light" ? "brand" : "brandDark"}
              />
              <FormCalcStatusList
                label="Applied Rate LLPA's"
                icon={<InfoCircleOutline marginRight={1} h={4} w={4} />}
                items={calculationWarnings?.rateLLPAsFactored}
                colorScheme={colorMode === "light" ? "brand" : "brandDark"}
              />
              <FormCalcStatusList
                label="Additional Pricing Details"
                icon={<InfoCircleOutline marginRight={1} h={4} w={4} />}
                items={
                  quoteType === "Rental"
                    ? (quoteCalculation as RentalCalculation).scenarios[0]
                        .pricingInfo
                    : quoteCalculation?.pricingInfo
                }
                colorScheme={colorMode === "light" ? "brand" : "brandDark"}
              />
            </VStack>

            {hasWarnings() && metadata && (
              <Divider borderColor="gray.400" marginTop={6} />
            )}
          </>

          {quoteCalculation && metadata && (
            <Text
              fontSize="xs"
              textAlign="center"
              marginTop={6}
              marginBottom={2}
            >
              Rates and Terms updated{" "}
              <Text
                as="span"
                color={colorMode === "dark" ? "brand.300" : "brand.400"}
                fontWeight="medium"
              >
                {formatDateLocal(metadata.pricingLastUpdated)}
              </Text>
            </Text>
          )}
        </>
      )}
    </Box>
  );
}

export default FormCalcSummary;
